export const SESSION_STORAGE_KEY = 'jst_auth_session';

export const EMAIL_CLAIM = 'https://api.jstscifree.com/claims/email';

export type SessionSource = 'passwordless' | 'e2e';

export interface StoredSession {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
  source: SessionSource;
}

export interface AuthUser {
  email?: string;
  [claim: string]: any;
}

export function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
}

const safeDecode = (token: string | undefined): Record<string, unknown> => {
  if (!token) return {};
  try {
    return decodeJwtPayload(token);
  } catch {
    return {};
  }
};

/**
 * The user comes from the access token, which carries the app_metadata and
 * email claims (the Post-Login Actions add them), so a passwordless session and
 * an e2e token are read the same way.
 */
export function userFromSession(session: StoredSession): AuthUser {
  const claims = safeDecode(session.accessToken);
  const idClaims = safeDecode(session.idToken);
  return {
    ...claims,
    email:
      (claims[EMAIL_CLAIM] as string | undefined) ??
      (idClaims.email as string | undefined),
  };
}

/** The access token's `exp`, in epoch milliseconds, or null if it has none. */
export function tokenExpiry(token: string): number | null {
  const { exp } = safeDecode(token);
  return typeof exp === 'number' ? exp * 1000 : null;
}

export function readSession(): StoredSession | null {
  const raw = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed?.accessToken ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSession(session: StoredSession | null): void {
  if (session) {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

export function isSessionUsable(
  session: StoredSession | null,
  now = Date.now()
): boolean {
  if (!session) return false;
  return session.expiresAt > now || !!session.refreshToken;
}
